import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
  TextField,
  Typography,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonGroup,
  Link
} from "@mui/material";

import HelpButton from "../../../components/utilities/HelpButton";
import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import { handlePagingAll } from "../../../components/api/APIUtilities";

import * as Yup from "yup";
import { Formik, FieldArray } from "formik";
import LocationPicker from "../../../components/utilities/LocationPicker";

import { globalVariables } from "../../../global/GlobalVariables";

import ImageUploader, {
  uploadBas64ToS3
} from "../../../components/utilities/ImageUploader";
import GenericPicker from "../../../components/utilities/GenericPicker";
import ModalLoadingMessage from "../../../components/modals/ModalLoadingMessage";
import {
  postData,
  titleCase
} from "../../../components/utilities/UtilityFunctions";
import TimePickerComponent from "../../../components/utilities/TimePickerComponent";
import moment from "moment";
import MUIPhoneInput from "../../../components/utilities/MUIPhoneInput";
import {
  ApprovalButton,
  PendingButton,
  RejectButton
} from "./ApprovalUtilities";
import { FormikContextHandler } from "../../../components/utilities/FormikUtilities";
import {
  BCPAngleDown,
  BCPArrowLeft,
  BCPCheck,
  BCPLockKeyhole,
  BCPLockKeyholeOpen
} from "../../../components/icons/bcp/BCPIcons";
import {
  getEnvironmentVariable,
  supportsUYPlatformFeatures
} from "../../../global/environmentVariables";
import { useTheme } from "@emotion/react";
import GenericMultiSelect from "../../../components/utilities/GenericMultiSelect";

const organizationTypes = [
  { value: "store", label: "Game Store" },
  { value: "club", label: "Gaming Club" },
  { value: "tournamentOrganizer", label: "Independent Tournament Organizer" },
  { value: "publisher", label: "Publisher" },
  { value: "other", label: "Other" }
];
const organizationTypesDict = {
  store: { id: "store", label: "Game Store" },
  club: { id: "club", label: "Gaming Club" },
  tournamentOrganizer: {
    id: "tournamentOrganizer",
    label: "Independent Tournament Organizer"
  },
  publisher: { id: "publisher", label: "Publisher" },
  other: { id: "other", label: "Other" }
};

const socialOptions = [
  { key: "Facebook", prefix: "facebook.com/" },
  { key: "Instagram", prefix: "instagram.com/" },
  { key: "Twitter", prefix: "twitter.com/" },
  { key: "Twitch", prefix: "twitch.tv/" },
  { key: "YouTube", prefix: "youtube.com/" },
  {
    key: "Discord",
    prefix: "Discord ID",
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <HelpButton
            title={`In Discord, select Settings > Advanced > Developer Mode. Once the Developer Mode is on, right click on your User Name in any chat window and select "Copy ID". This should be a string of 18 numbers`}
          />
        </InputAdornment>
      )
    }
  }
];

const days = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];

const SUPPORTS_UY_PLATFORM_FEATURES = supportsUYPlatformFeatures();

export default function OrganizationDetails(props) {
  const { t } = useTranslation();
  const { organization, setOrganization, authContext } = props;

  const globalVars = globalVariables();
  const theme = useTheme();

  const [initialValues, setInitialValues] = useState(null);
  const [validationSchema, setValidationSchema] = useState(null);
  const [readOnly, setReadOnly] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  const [loading, setLoading] = useState(false);
  const [processingMessage, setProcessingMessage] = useState(null);

  const [locations, setLocations] = useState(null);

  const [expandLocation, setExpandLocation] = useState(
    Boolean(organization?.id)
  );
  const [expandHours, setExpandHours] = useState(Boolean(organization?.id));

  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0
  });

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      if (!locations) {
        try {
          var apiLocations = await handlePagingAll("/locations", {
            userId: authContext?.authState?.identity?.userId
          });

          apiLocations.sort((a, b) => {
            if (organization?.locationId) {
              if (a.id === organization.locationId) return 1;
              if (b.id === organization.locationId) return -1;
            }
            if (a.name && b.name) {
              var nameA = a.name.toLowerCase();
              var nameB = b.name.toLowerCase();
              if (nameA > nameB) return 1;
              if (nameA < nameB) return -1;
            } else if (a.name) {
              return 1;
            } else if (b.name) {
              return -1;
            }
            return 0;
          });

          if (organization) {
            if (organization?.location) {
              if (!apiLocations?.length) {
                apiLocations = [organization.location];
              } else if (
                apiLocations.filter((e) => e.id === organization.locationId)
                  .length === 0
              ) {
                apiLocations = [organization.location, ...apiLocations];
              }
            }
          }
          setLocations(apiLocations);
        } catch (err) {
          postNotification("error", err);
        }
      }

      var validationSchema = {
        name: Yup.string()
          .min(3, "Minimum 3 Characters!")
          .max(255, "Maximum 255 Characters!")
          .required("Required"),
        locationId: Yup.string().required("Required")
      };

      var initialValues = {
        name: organization?.name || "",
        locationId: organization?.locationId || "",

        websiteURL: organization?.websiteURL || "",
        defaultExternalURL: organization?.defaultExternalURL || "",
        socials: organization?.socials || {},

        description: organization?.description || "",

        organizationType: organization?.organizationType || "store",
        organizationTypes: {
          store: organization.organizationTypes?.store || false,
          club: organization.organizationTypes?.club || false,
          tournamentOrganizer:
            organization.organizationTypes?.tournamentOrganizer || false,
          publisher: organization.organizationTypes?.publisher || false,
          other: organization.organizationTypes?.other || false
        },
        openingHours:
          organization?.openingHours !== undefined
            ? organization.openingHours
            : {}
      };

      // Create list of organization types for generic picker
      initialValues.organizationTypesList = Object.keys(
        initialValues.organizationTypes
      ).map((key) => {
        const label =
          key === "store"
            ? "Game Store"
            : key === "club"
              ? "Gaming Club"
              : key === "tournamentOrganizer"
                ? "Independent Tournament Organizer"
                : key === "publisher"
                  ? "Publisher"
                  : key === "other"
                    ? "Other"
                    : titleCase(key);

        return {
          label: label,
          value: key
        };
      });
      initialValues.organizationTypesArray = Object.keys(
        initialValues.organizationTypes
      ).filter((key) => {
        if (initialValues.organizationTypes[key]) {
          return true;
        }
        return false;
      });
      for (let day of days) {
        if (!initialValues.openingHours[day]) {
          initialValues.openingHours[day] = {
            open: false,
            hours: []
          };
        }
      }

      if (
        authContext?.authState?.identity?.isAdmin === true ||
        organization?.active
      ) {
        initialValues.active =
          organization?.active !== undefined ? organization.active : false;
      }

      if (globalVars?.onboarding?.gameStore) {
        initialValues.onboarding = {};
        var onboardingValidationSchema = {};
        Object.keys(globalVars.onboarding.gameStore).forEach((fieldId) => {
          var field = globalVars.onboarding.gameStore[fieldId];
          if (
            field.type === "list" ||
            field.type === "text" ||
            field.type === "phone" ||
            field.type === "radio"
          ) {
            initialValues.onboarding[fieldId] =
              organization?.onboarding?.[fieldId] || "";
            onboardingValidationSchema[fieldId] = field.required
              ? Yup.string()
                  .max(255, "Maximum 255 Characters!")
                  .required("Required")
              : Yup.string().max(255, "Maximum 255 Characters!");
            if (field.type === "list") {
              initialValues.onboarding[`${fieldId}Secondary`] =
                organization?.onboarding?.[`${fieldId}Secondary`] || "";
            }
          } else if (field.type === "email") {
            initialValues.onboarding[fieldId] =
              organization?.onboarding?.[fieldId] || "";
            onboardingValidationSchema[fieldId] = field.required
              ? Yup.string()
                  .max(128)
                  .email("Must be a valid email")
                  .required("Required")
              : Yup.string().max(128).email("Must be a valid email");
          } else if (field.type === "checkbox") {
            initialValues.onboarding[fieldId] =
              organization?.onboarding?.[fieldId] || false;
            if (field.required) {
              onboardingValidationSchema[fieldId] = Yup.boolean().oneOf(
                [true],
                "Required"
              );
            }
          } else if (field.type === "gallery") {
            initialValues.onboarding[fieldId] = organization?.onboarding?.[
              fieldId
            ] || [{}];
            if (field.required) {
              onboardingValidationSchema[fieldId] = Yup.array()
                .min(1)
                .required();
            }
          } else {
            initialValues.onboarding[fieldId] =
              organization?.onboarding?.[fieldId];
            initialValues.onboarding[`${fieldId}Secondary`] =
              organization?.onboarding?.[`${fieldId}Secondary`] || "";
          }
        });

        validationSchema.onboarding = Yup.object().shape(
          onboardingValidationSchema
        );
      }

      setInitialValues(initialValues);
      setValidationSchema(validationSchema);

      if (
        !readOnly ||
        !organization?.id ||
        authContext?.authState?.identity?.isAdmin !== true
      ) {
        setReadOnly(false);
      }
      setLoading(false);
    };

    check();
    // eslint-disable-next-line
  }, [organization]);

  return (
    <Box sx={{ width: "100%" }}>
      <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
        <Grid container sx={{ p: 2, gap: 2 }}>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Link
              href="/organize/game-stores"
              color="link.main"
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start"
              }}
            >
              <BCPArrowLeft
                sx={{ "padding-right": "1rem", "padding-bottom": "2px" }}
              />{" "}
              Back to My Organizations
            </Link>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="h2"
              component="h3"
              fontWeight="700"
              gutterBottom
              sx={{ mb: 4 }}
            >
              {organization?.id ? "Edit Organization" : "Create Organization"}
            </Typography>
          </Grid>

          {!organization?.id && (
            <Grid item xs={12} sx={{ mb: 2 }}>
              <Typography gutterBottom>
                {`Creating an organization in ${getEnvironmentVariable(
                  "COMPANY_NAME"
                )} for your game store, gaming club, or other form of organizing group will help you raise visibility to your activities and businesses. `}
              </Typography>
            </Grid>
          )}

          {authContext?.authState?.identity?.isAdmin === true && (
            <Grid item xs={12}>
              <Typography variant="h5">
                <Button
                  color="primary"
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setReadOnly(!readOnly);
                  }}
                  endIcon={
                    readOnly ? (
                      <BCPLockKeyhole outlined />
                    ) : (
                      <BCPLockKeyholeOpen outlined />
                    )
                  }
                >
                  {readOnly ? "Locked" : "Unlocked"}
                </Button>
              </Typography>

              <Grid container spacing={1} sx={{ mt: 1, mb: 2 }}>
                <Grid item xs={12} lg={4}>
                  <RejectButton
                    dirty={isDirty}
                    organization={organization}
                    handleUpdates={(updates) => {
                      setOrganization({ ...organization, ...updates });
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <PendingButton
                    dirty={isDirty}
                    organization={organization}
                    handleUpdates={(updates) => {
                      setOrganization({ ...organization, ...updates });
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <ApprovalButton
                    dirty={isDirty}
                    organization={organization}
                    handleUpdates={(updates) => {
                      var newOrg = { ...organization, ...updates };
                      delete newOrg.approvalReason;
                      delete newOrg.approvalReasons;
                      setOrganization(newOrg);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          {initialValues && (
            <Grid item xs={12}>
              <Formik
                initialValues={initialValues}
                enableReinitialize
                validationSchema={Yup.object().shape(validationSchema)}
                onSubmit={async (values, { resetForm }) => {
                  setProcessingMessage("saving...");
                  try {
                    var body;
                    var sanitizedValues = {
                      ...values
                    };

                    if (!sanitizedValues.organizationTypesArray?.length) {
                      throw new Error(
                        "Please select at least one Organization type (store or club)"
                      );
                    }

                    // Rebuild from the picker so deselected types are saved as false
                    sanitizedValues.organizationTypes = {};
                    for (const { value } of values.organizationTypesList) {
                      sanitizedValues.organizationTypes[value] =
                        sanitizedValues.organizationTypesArray.includes(value);
                    }

                    // Sets the organization type to the selected type
                    // if (
                    //   sanitizedValues?.organizationTypes &&
                    //   sanitizedValues.organizationType
                    // ) {
                    //   for (const key in sanitizedValues.organizationTypes) {
                    //     if (key === sanitizedValues.organizationType) {
                    //       sanitizedValues.organizationTypes[key] = true;

                    //       for (const otherKey in sanitizedValues.organizationTypes) {
                    //         if (otherKey !== key) {
                    //           sanitizedValues.organizationTypes[otherKey] =
                    //             false;
                    //         }
                    //       }

                    //       break;
                    //     }
                    //   }
                    // }

                    if (sanitizedValues.organizationType)
                      delete sanitizedValues.organizationType;

                    if (sanitizedValues?.organizationTypesList)
                      delete sanitizedValues.organizationTypesList;

                    if (sanitizedValues?.organizationTypesArray)
                      delete sanitizedValues.organizationTypesArray;

                    // check onboarding "others"
                    if (globalVars.onboarding?.gameStore) {
                      Object.keys(globalVars.onboarding.gameStore).forEach(
                        (fieldId) => {
                          var field = globalVars.onboarding.gameStore[fieldId];
                          if (field.required) {
                            if (field.type === "list") {
                              if (!sanitizedValues.onboarding[fieldId]) {
                                throw new Error(
                                  `Missing required field: ${field.label}`
                                );
                              }

                              if (
                                !sanitizedValues.onboarding[
                                  `${fieldId}Secondary`
                                ]
                              ) {
                                var selectedItem = field.list.filter(
                                  (el) =>
                                    el.label ===
                                    sanitizedValues.onboarding[fieldId]
                                )[0];
                                if (!selectedItem) {
                                  throw new Error(
                                    `Error with required field: ${field.label}`
                                  );
                                }

                                if (selectedItem?.secondaryLabel) {
                                  throw new Error(
                                    `Missing required field: ${selectedItem.secondaryLabel}`
                                  );
                                }
                              }
                            } else if (field.type === "gallery") {
                              if (
                                !sanitizedValues.onboarding[fieldId]?.length ||
                                !sanitizedValues.onboarding[fieldId].filter(
                                  (el) => {
                                    return el.photoFileId || el.imageURL;
                                  }
                                )?.length
                              ) {
                                throw new Error(
                                  `Missing required field: ${
                                    field.error || field.label
                                  }`
                                );
                              }
                            }
                          }
                        }
                      );
                    }

                    if (sanitizedValues._p_profileFile) {
                      setProcessingMessage("Uploading profile image");
                      // upload image to s3
                      const newFile = await uploadBas64ToS3(
                        sanitizedValues._p_profileFile
                      );
                      delete sanitizedValues._p_profileFile;
                      if (newFile.fileId) {
                        sanitizedValues.profileFileId = newFile.fileId;
                      }
                    }

                    if (sanitizedValues.organizationImage) {
                      setProcessingMessage("Uploading banner image");
                      // upload image to s3
                      const newFile = await uploadBas64ToS3(
                        sanitizedValues.organizationImage
                      );
                      delete sanitizedValues.organizationImage;
                      if (newFile.fileId) {
                        sanitizedValues.photoFileId = newFile.fileId;
                      }
                    }

                    if (globalVars?.onboarding?.gameStore) {
                      var onboardingKeys = Object.keys(
                        globalVars.onboarding.gameStore
                      );
                      for (var oi = 0; oi < onboardingKeys.length; oi++) {
                        var fieldId = onboardingKeys[oi];
                        var field = globalVars.onboarding.gameStore[fieldId];
                        if (
                          field.type === "gallery" &&
                          sanitizedValues.onboarding[fieldId]?.length > 0
                        ) {
                          var numImages = sanitizedValues.onboarding[
                            fieldId
                          ].filter((el) => {
                            return el.imageURL;
                          })?.length;
                          var imageCount = 1;

                          for (
                            var gi = 0;
                            gi < sanitizedValues.onboarding[fieldId].length;
                            gi++
                          ) {
                            if (
                              sanitizedValues.onboarding[fieldId][gi].imageURL
                            ) {
                              // upload image to s3
                              setProcessingMessage(
                                `Uploading onboarding image ${imageCount} of ${numImages}`
                              );
                              imageCount++;
                              var newGalleryItem = await uploadBas64ToS3(
                                sanitizedValues.onboarding[fieldId][gi].imageURL
                              );
                              delete sanitizedValues.onboarding[fieldId][gi]
                                .imageURL;
                              if (newGalleryItem.fileId) {
                                sanitizedValues.onboarding[fieldId][
                                  gi
                                ].photoFileId = newGalleryItem.fileId;
                              }
                            }
                          }
                        }
                      }
                    }
                    setProcessingMessage("saving...");

                    if (organization?.id) {
                      body = {
                        set: sanitizedValues
                      };
                      var apiOrganization = await postData(
                        `/gamestores/${organization.id}`,
                        {
                          body: body
                        }
                      );

                      if (apiOrganization && !apiOrganization?.error) {
                        setOrganization({
                          ...organization,
                          ...apiOrganization
                        });
                        postNotification(
                          "success",
                          t("notifications.success.organizationUpdated")
                        );
                      } else {
                        throw new Error(
                          apiOrganization?.error ||
                            "Failed to update orgnanization. Please try again later."
                        );
                      }
                    } else {
                      sanitizedValues.ownerId =
                        authContext?.authState?.identity?.userId;

                      var apiData = await postData(`/gamestores`, {
                        body: sanitizedValues
                      });

                      if (apiData && !apiData?.error) {
                        postNotification(
                          "success",
                          t("notifications.success.organizationCreated")
                        );
                        window.location.assign(
                          `/organize/organization/${apiData.id}`
                        );
                      } else {
                        throw new Error(
                          apiData?.error ||
                            "Failed to create organization. Please try again later."
                        );
                      }
                    }
                  } catch (err) {
                    postNotification("error", err);
                  }
                  setProcessingMessage(null);
                  return;
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue,
                  setFieldTouched,
                  dirty
                }) => (
                  <form onSubmit={handleSubmit} noValidate>
                    <FormikContextHandler dirty={dirty} setDirty={setIsDirty} />
                    <Grid container spacing={1}>
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1
                        }}
                      >
                        <Typography
                          variant="h4"
                          gutterBottom
                          sx={{ textAlign: "center" }}
                        >
                          Profile Image
                        </Typography>

                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                          }}
                        >
                          <ImageUploader
                            values={values}
                            label={values.name}
                            name="_p_profileFile"
                            setFieldValue={setFieldValue}
                            photoFileId={organization?.profile?.photoFileId}
                            defaultUrl={
                              organization?.profile?.url ||
                              organization?.profileImage ||
                              organization?.image?.profileUrl
                            }
                            disabled={readOnly}
                            aspectY={1}
                            aspectW={1}
                            sx={{
                              maxHeight: ["200px", "250px"],
                              maxWidth: ["200px", "250px"]
                            }}
                            round
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={9}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{ textAlign: "center" }}
                          gutterBottom
                        >
                          Banner Image
                        </Typography>
                        <ImageUploader
                          values={values}
                          label={values.name}
                          name="organizationImage"
                          setFieldValue={setFieldValue}
                          photoFileId={organization?.photoFileId}
                          defaultUrl={
                            organization?.photo?.url ||
                            organization?.photoUrl ||
                            organization?.imageURL
                          }
                          disabled={readOnly}
                          aspectY={9}
                          aspectW={21}
                          sx={{ maxHeight: "250px" }}
                          matchAspectRatio
                        />
                      </Grid>

                      <Grid item xs={12} mt={2}>
                        <Typography
                          sx={{
                            color: readOnly ? "text.disabled" : "inherit"
                          }}
                          variant="h4"
                          gutterBottom
                          fontWeight="700"
                        >
                          About
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="name"
                          label="Organization Name"
                          value={values.name}
                          variant="outlined"
                          fullWidth
                          required
                          margin="dense"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                          disabled={
                            (authContext?.authState?.identity?.isAdmin !==
                              true &&
                              organization?.id) ||
                            readOnly
                          }
                        />
                      </Grid>

                      {!SUPPORTS_UY_PLATFORM_FEATURES && (
                        <Grid item xs={12}>
                          <GenericMultiSelect
                            name="organizationTypesArray"
                            label="Organization Type"
                            value={values.organizationTypesArray}
                            setFieldValue={setFieldValue}
                            listDict={organizationTypesDict}
                            list={organizationTypes}
                            error={Boolean(
                              touched.organizationType &&
                                errors.organizationType
                            )}
                            helperText={
                              touched.organizationType &&
                              errors.organizationType
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          name="description"
                          label="Description"
                          value={values.description}
                          fullWidth
                          multiline
                          rows={5}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.description && errors.description
                          )}
                          helperText={touched.description && errors.description}
                          disabled={readOnly}
                        />
                      </Grid>

                      {(authContext?.authState?.identity?.isAdmin === true ||
                        initialValues.active) && (
                        <Grid item xs={12}>
                          <FormControlLabel
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                Active{" "}
                                <HelpButton title="Organizations that are inactive are only visible to Organization Owners" />
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)"
                              }
                            }}
                            control={
                              <Switch
                                color="primary"
                                checked={values.active}
                                onChange={(e) => {
                                  setFieldValue("active", !values.active);
                                }}
                                disabled={readOnly}
                              />
                            }
                            labelPlacement="start"
                          />
                        </Grid>
                      )}

                      {/* TODO: Will need to remove the required field for location if we want to implement this */}
                      <Grid item xs={12} mt={2}>
                        <Typography
                          sx={{
                            color: readOnly ? "text.disabled" : "inherit"
                          }}
                          variant="h4"
                          gutterBottom
                          fontWeight="700"
                        >
                          Location
                        </Typography>
                      </Grid>

                      {/* {!Boolean(organization?.id) && (
                        <>
                          <Grid item xs={12}>
                            <Typography
                              sx={{
                                color: readOnly ? "text.disabled" : "inherit",
                              }}
                            >
                              Does your organization have a physical location?
                            </Typography>
                          </Grid>

                          <Grid item xs={6} md={4} sx={{ mb: 1 }}>
                            <ButtonGroup fullWidth>
                              <Button
                                startIcon={
                                  expandLocation && <BCPCheck size={20} />
                                }
                                variant={
                                  expandLocation ? "contained" : "outlined"
                                }
                                disabled={readOnly}
                                onClick={() => setExpandLocation(true)}
                                fullWidth
                              >
                                Yes
                              </Button>
                              <Button
                                startIcon={
                                  !expandLocation && <BCPCheck size={20} />
                                }
                                variant={
                                  !expandLocation ? "contained" : "outlined"
                                }
                                disabled={readOnly}
                                onClick={() => setExpandLocation(false)}
                                fullWidth
                              >
                                No
                              </Button>
                            </ButtonGroup>
                          </Grid>
                        </>
                      )} */}

                      {/* TODO: Temporary logic, if we want to allow this interaction then Location needs to be made optional */}
                      {(expandLocation || true) && (
                        <Grid item xs={12}>
                          <LocationPicker
                            locationId={values.locationId}
                            required
                            locations={locations}
                            setLocations={setLocations}
                            setLocation={(location) => {
                              setFieldValue("location", location);
                              setFieldValue("locationId", location?.id);
                              var newLocations = [...locations];
                              var found = false;
                              newLocations.forEach((tempLocation) => {
                                if (tempLocation.id === location?.id) {
                                  found = true;
                                  tempLocation = {
                                    ...tempLocation,
                                    ...location
                                  };
                                  return true;
                                }
                              });
                              if (!found) {
                                newLocations.push(location);
                              }
                              setLocations(newLocations);
                            }}
                            error={Boolean(
                              touched.locationId && errors.locationId
                            )}
                            helperText={touched.locationId && errors.locationId}
                            setFieldTouched={() =>
                              setFieldTouched("locationId")
                            }
                            disabled={readOnly}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          name="websiteURL"
                          label="Website URL"
                          value={values.websiteURL}
                          variant="outlined"
                          fullWidth
                          margin="dense"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.websiteURL && errors.websiteURL
                          )}
                          helperText={touched.websiteURL && errors.websiteURL}
                          InputProps={{
                            sx: {
                              cursor: "pointer",
                              "& input": {
                                cursor: "pointer"
                              }
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <HelpButton title="This is the organization's website, which can be viewed from the organization's landing page." />
                              </InputAdornment>
                            )
                          }}
                          disabled={readOnly}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="defaultExternalURL"
                          label="Default External Link"
                          value={values.defaultExternalURL}
                          variant="outlined"
                          fullWidth
                          margin="dense"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.defaultExternalURL &&
                              errors.defaultExternalURL
                          )}
                          helperText={
                            touched.defaultExternalURL &&
                            errors.defaultExternalURL
                          }
                          InputProps={{
                            sx: {
                              cursor: "pointer",
                              "& input": {
                                cursor: "pointer"
                              }
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <HelpButton title="When an event is assigned to this organization, the event's external url will default to this value." />
                              </InputAdornment>
                            )
                          }}
                          disabled={readOnly}
                        />
                      </Grid>

                      {socialOptions?.length && (
                        <Grid item xs={12}>
                          <Accordion
                            sx={{
                              width: "100%",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "background.secondary"
                                  : "background.primary"
                            }}
                            disabled={readOnly}
                          >
                            <AccordionSummary
                              expandIcon={
                                <BCPAngleDown
                                  style={{
                                    "font-size": "14px",
                                    width: "14px"
                                  }}
                                />
                              }
                              aria-controls="socials-content"
                              id="socials-header"
                            >
                              <Typography>Socials</Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                              {socialOptions.map((socialOption, index) => {
                                const {
                                  key,
                                  //  prefix,
                                  InputProps
                                } = socialOption;
                                return (
                                  <Grid item xs={12} key={index}>
                                    <TextField
                                      name={`socials.${key}`}
                                      value={values.socials?.[key]}
                                      label={key}
                                      variant="outlined"
                                      color="primary"
                                      margin="dense"
                                      fullWidth
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      disabled={readOnly}
                                      InputProps={{
                                        ...InputProps
                                      }}
                                    />
                                  </Grid>
                                );
                              })}
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      )}

                      <Grid item xs={12} mt={4}>
                        <Typography
                          variant="h4"
                          sx={{ color: readOnly ? "text.disabled" : "inherit" }}
                          gutterBottom
                          fontWeight="700"
                        >
                          Hours
                        </Typography>
                      </Grid>

                      {!Boolean(organization?.id) && (
                        <>
                          <Grid item xs={12}>
                            <Typography
                              sx={{
                                color: readOnly ? "text.disabled" : "inherit"
                              }}
                            >
                              Does your organization have hours of operation?
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={4}>
                            <ButtonGroup fullWidth>
                              <Button
                                startIcon={
                                  expandHours && <BCPCheck size={20} />
                                }
                                variant={expandHours ? "contained" : "outlined"}
                                disabled={readOnly}
                                onClick={() => setExpandHours(true)}
                                fullWidth
                              >
                                Yes
                              </Button>
                              <Button
                                startIcon={
                                  !expandHours && <BCPCheck size={20} />
                                }
                                variant={
                                  !expandHours ? "contained" : "outlined"
                                }
                                disabled={readOnly}
                                onClick={() => setExpandHours(false)}
                                fullWidth
                              >
                                No
                              </Button>
                            </ButtonGroup>
                          </Grid>
                        </>
                      )}

                      {expandHours && (
                        <Grid item xs={12}>
                          <Grid container>
                            {days.map((day, dayIndex) => (
                              <>
                                <Grid
                                  key={dayIndex}
                                  item
                                  xs={6}
                                  md={2}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center"
                                  }}
                                >
                                  <FormControlLabel
                                    label={
                                      <Typography
                                        className="MuiFormControlLabel-label"
                                        sx={{ fontWeight: [700, 500] }}
                                      >
                                        {titleCase(day)}
                                      </Typography>
                                    }
                                    sx={{
                                      ml: 0,
                                      width: "100%",
                                      "& .MuiFormControlLabel-label": {
                                        width: "calc(100% - 58px)"
                                      },

                                      display: "flex",
                                      alignItems: "center"
                                    }}
                                    control={
                                      <Checkbox
                                        color="secondary"
                                        checked={
                                          values.openingHours?.[day]?.open
                                        }
                                        onChange={(e) => {
                                          if (
                                            values.openingHours?.[day]?.open
                                          ) {
                                            setFieldValue(
                                              `openingHours.${day}.hours`,
                                              []
                                            );
                                          } else {
                                            setFieldValue(
                                              `openingHours.${day}.hours`,
                                              [
                                                {
                                                  open: "09:00",
                                                  close: "17:00"
                                                }
                                              ]
                                            );
                                          }
                                          setFieldValue(
                                            `openingHours.${day}.open`,
                                            !values.openingHours?.[day]?.open
                                          );
                                        }}
                                        disabled={readOnly}
                                      />
                                    }
                                  />
                                </Grid>
                                {values.openingHours?.[day]?.open ? (
                                  <Grid item xs={12} md={10}>
                                    <FieldArray
                                      name={`openingHours.${day}.hours`}
                                      render={(arrayHelpers) => (
                                        <>
                                          {values.openingHours[day].hours.map(
                                            (hour, index) => {
                                              let open = hour?.open || "09:00";
                                              open = open.split(":");
                                              open = moment()
                                                .hour(
                                                  open[0] !== undefined
                                                    ? open[0]
                                                    : 9
                                                )
                                                .minute(
                                                  open[1] !== undefined
                                                    ? open[1]
                                                    : 0
                                                );

                                              let close =
                                                hour?.close || "17:00";
                                              close = close.split(":");
                                              close = moment()
                                                .hour(
                                                  close[0] !== undefined
                                                    ? close[0]
                                                    : 9
                                                )
                                                .minute(
                                                  close[1] !== undefined
                                                    ? close[1]
                                                    : 0
                                                );

                                              return (
                                                <Grid container spacing={1}>
                                                  <Grid
                                                    key={`${index}-O`}
                                                    item
                                                    xs={6}
                                                    md={6}
                                                  >
                                                    <TimePickerComponent
                                                      name={`openingHours.${day}.hours[${index}].open`}
                                                      label="Opening Time"
                                                      value={open}
                                                      setFieldValue={
                                                        setFieldValue
                                                      }
                                                      setFieldTouched={
                                                        setFieldTouched
                                                      }
                                                      disabled={readOnly}
                                                    />
                                                  </Grid>

                                                  <Grid
                                                    key={`${index}-C`}
                                                    item
                                                    xs={6}
                                                    md={6}
                                                  >
                                                    <TimePickerComponent
                                                      name={`openingHours.${day}.hours[${index}].close`}
                                                      label="Closing Time"
                                                      value={close}
                                                      setFieldValue={
                                                        setFieldValue
                                                      }
                                                      setFieldTouched={
                                                        setFieldTouched
                                                      }
                                                      disabled={readOnly}
                                                    />
                                                  </Grid>
                                                </Grid>
                                              );
                                            }
                                          )}
                                        </>
                                      )}
                                    />
                                  </Grid>
                                ) : (
                                  <Grid
                                    item
                                    xs={6}
                                    md={10}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center"
                                    }}
                                  >
                                    <Typography>Closed</Typography>
                                  </Grid>
                                )}
                              </>
                            ))}
                          </Grid>
                        </Grid>
                      )}

                      {globalVars?.onboarding?.gameStore && (
                        <>
                          <Grid item xs={12} mt={4}>
                            <Typography
                              variant="h4"
                              sx={{
                                color: readOnly ? "text.disabled" : "inherit"
                              }}
                              gutterBottom
                              fontWeight="700"
                            >
                              Onboarding Information
                            </Typography>
                          </Grid>
                          {Object.keys(globalVars.onboarding.gameStore).map(
                            (fieldId, index) => {
                              var field =
                                globalVars.onboarding.gameStore[fieldId];
                              if (field.type === "label") {
                                return (
                                  <Grid item xs={12}>
                                    <Typography
                                      variant="body1"
                                      gutterBottom
                                      sx={{
                                        fontWeight: field.bold
                                          ? "bold"
                                          : "inherit"
                                      }}
                                    >
                                      {field.label}
                                    </Typography>
                                    {field.helperText && (
                                      <FormHelperText>
                                        {field.helperText}
                                      </FormHelperText>
                                    )}
                                  </Grid>
                                );
                              }

                              if (
                                field.type === "text" ||
                                field.type === "email"
                              ) {
                                return (
                                  <Grid item xs={12}>
                                    <TextField
                                      name={`onboarding.${fieldId}`}
                                      label={field.label}
                                      value={values.onboarding?.[fieldId]}
                                      variant="outlined"
                                      fullWidth
                                      margin="dense"
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      required={field.required}
                                      error={Boolean(
                                        touched.onboarding?.[fieldId] &&
                                          errors.onboarding?.[fieldId]
                                      )}
                                      helperText={
                                        touched.onboarding?.[fieldId] &&
                                        errors.onboarding?.[fieldId]
                                      }
                                      disabled={readOnly}
                                    />
                                    {field.helperText && (
                                      <FormHelperText>
                                        {field.helperText}
                                      </FormHelperText>
                                    )}
                                  </Grid>
                                );
                              }

                              if (field.type === "phone") {
                                return (
                                  <Grid item xs={12}>
                                    <MUIPhoneInput
                                      name={`onboarding.${fieldId}`}
                                      label={field.label}
                                      value={values.onboarding?.[fieldId]}
                                      variant="outlined"
                                      fullWidth
                                      margin="dense"
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      required={field.required}
                                      error={Boolean(
                                        touched.onboarding?.[fieldId] &&
                                          errors.onboarding?.[fieldId]
                                      )}
                                      helperText={
                                        touched.onboarding?.[fieldId] &&
                                        errors.onboarding?.[fieldId]
                                      }
                                      disabled={readOnly}
                                    />
                                  </Grid>
                                );
                              }

                              if (field.type === "checkbox") {
                                return (
                                  <Grid item xs={12}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          name={`onboarding.${fieldId}`}
                                          checked={values.onboarding?.[fieldId]}
                                          defaultChecked={false}
                                          onChange={(e) => {
                                            setFieldValue(
                                              `onboarding.${fieldId}`,
                                              !values.onboarding?.[fieldId]
                                            );
                                          }}
                                          disabled={readOnly}
                                          required={field.required}
                                        />
                                      }
                                      label={
                                        field.label +
                                        (field.required ? " *" : "")
                                      }
                                    />

                                    {field.helperText && (
                                      <FormHelperText>
                                        {field.helperText}
                                      </FormHelperText>
                                    )}
                                    {Boolean(
                                      touched.onboarding?.[fieldId] &&
                                        errors.onboarding?.[fieldId]
                                    ) && (
                                      <FormHelperText error>
                                        {errors.onboarding?.[fieldId]}
                                      </FormHelperText>
                                    )}
                                  </Grid>
                                );
                              }

                              if (field.type === "radio") {
                                return (
                                  <Grid item xs={12}>
                                    <FormControl>
                                      <FormLabel>{field.label}</FormLabel>
                                      {field.helperText && (
                                        <FormHelperText>
                                          {field.helperText}
                                        </FormHelperText>
                                      )}
                                      <RadioGroup
                                        row
                                        defaultValue={
                                          values.onboarding?.[fieldId]
                                        }
                                      >
                                        {field.options.map((option, index) => {
                                          return (
                                            <FormControlLabel
                                              key={index}
                                              value={option}
                                              control={
                                                <Radio
                                                  name={`onboarding.${fieldId}`}
                                                  checked={
                                                    values.onboarding?.[
                                                      fieldId
                                                    ] === option
                                                  }
                                                  defaultChecked={false}
                                                  onClick={(e) => {
                                                    if (
                                                      values.onboarding?.[
                                                        fieldId
                                                      ] === option
                                                    ) {
                                                      setFieldValue(
                                                        `onboarding.${fieldId}`,
                                                        null
                                                      );
                                                    } else {
                                                      setFieldValue(
                                                        `onboarding.${fieldId}`,
                                                        option
                                                      );
                                                    }
                                                  }}
                                                  disabled={readOnly}
                                                />
                                              }
                                              label={option}
                                            />
                                          );
                                        })}
                                      </RadioGroup>
                                    </FormControl>
                                    {Boolean(
                                      touched.onboarding?.[fieldId] &&
                                        errors.onboarding?.[fieldId]
                                    ) && (
                                      <FormHelperText error>
                                        {errors.onboarding?.[fieldId]}
                                      </FormHelperText>
                                    )}
                                  </Grid>
                                );
                              }

                              if (field.type === "gallery") {
                                return (
                                  <Grid item xs={12}>
                                    <Typography variant="body1" gutterBottom>
                                      {field.label}
                                      {field.required && " *"}
                                    </Typography>
                                    {field.helperText && (
                                      <FormHelperText>
                                        {field.helperText}
                                      </FormHelperText>
                                    )}
                                    <Grid container spacing={1}>
                                      <FieldArray
                                        name={`onboarding.${fieldId}`}
                                        render={(arrayHelpers) => (
                                          <>
                                            {values.onboarding?.[fieldId]
                                              ?.length > 0 &&
                                              values.onboarding[fieldId].map(
                                                (picture, rowIndex) => {
                                                  return (
                                                    <Grid item xs={12}>
                                                      <ImageUploader
                                                        label={field.label}
                                                        name={`onboarding.${fieldId}[${rowIndex}].imageURL`}
                                                        fileId={
                                                          values.onboarding[
                                                            fieldId
                                                          ][rowIndex]
                                                            .photoFileId || null
                                                        }
                                                        setFieldValue={
                                                          setFieldValue
                                                        }
                                                        defaultUrl={
                                                          values.onboarding[
                                                            fieldId
                                                          ][rowIndex].photo
                                                            ?.url ||
                                                          values.onboarding[
                                                            fieldId
                                                          ][rowIndex].imageURL
                                                        }
                                                        noAspect
                                                        disabled={readOnly}
                                                      />
                                                    </Grid>
                                                  );
                                                }
                                              )}
                                            {!readOnly &&
                                              values.onboarding?.[fieldId]
                                                ?.length < (field.max || 5) && (
                                                <Grid item xs={12}>
                                                  <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    fullWidth
                                                    onClick={() =>
                                                      arrayHelpers.push({})
                                                    }
                                                  >
                                                    Add Image
                                                  </Button>
                                                </Grid>
                                              )}
                                          </>
                                        )}
                                      />
                                    </Grid>
                                  </Grid>
                                );
                              }

                              if (field.type === "list") {
                                return (
                                  <>
                                    <Grid item xs={12}>
                                      <GenericPicker
                                        name={`onboarding.${fieldId}`}
                                        label={field.label}
                                        value={values.onboarding?.[fieldId]}
                                        inclAny={false}
                                        handleChange={(e) => {
                                          handleChange(e);
                                          var selField =
                                            e.target.value &&
                                            e.target.value !== ""
                                              ? field.list.filter(
                                                  (el) =>
                                                    el.label === e.target.value
                                                )[0]
                                              : null;
                                          if (!selField?.secondaryLabel) {
                                            setFieldValue(
                                              `onboarding.${fieldId}Secondary`,
                                              ""
                                            );
                                          }
                                        }}
                                        required={field.required}
                                        touched={touched.onboarding?.[fieldId]}
                                        error={errors.onboarding?.[fieldId]}
                                        disabled={readOnly}
                                        list={field.list.map((el) => el.label)}
                                      />
                                      {field.helperText && (
                                        <FormHelperText>
                                          {field.helperText}
                                        </FormHelperText>
                                      )}
                                    </Grid>
                                    {field.list
                                      .filter(
                                        (el) =>
                                          el.secondaryLabel &&
                                          el.label ===
                                            values.onboarding?.[fieldId]
                                      )
                                      .map((el) => {
                                        return (
                                          <Grid item xs={12}>
                                            <TextField
                                              name={`onboarding.${fieldId}Secondary`}
                                              label={el.secondaryLabel}
                                              value={
                                                values.onboarding?.[
                                                  `${fieldId}Secondary`
                                                ]
                                              }
                                              variant="outlined"
                                              fullWidth
                                              margin="dense"
                                              onBlur={handleBlur}
                                              onChange={handleChange}
                                              required={field.required}
                                              error={Boolean(
                                                touched.onboarding?.[
                                                  `${fieldId}Secondary`
                                                ] &&
                                                  errors.onboarding?.[
                                                    `${fieldId}Secondary`
                                                  ]
                                              )}
                                              helperText={
                                                touched.onboarding?.[
                                                  `${fieldId}Secondary`
                                                ] &&
                                                errors.onboarding?.[
                                                  `${fieldId}Secondary`
                                                ]
                                              }
                                              disabled={readOnly}
                                            />
                                            {el.secondaryHelperText && (
                                              <FormHelperText>
                                                {el.secondaryHelperText}
                                              </FormHelperText>
                                            )}
                                          </Grid>
                                        );
                                      })}
                                  </>
                                );
                              }

                              if (field.type === "socials") {
                                return (
                                  <>
                                    {socialOptions.map(
                                      (socialOption, index) => {
                                        const {
                                          key,
                                          //  prefix,
                                          InputProps
                                        } = socialOption;
                                        return (
                                          <Grid item xs={12} key={index}>
                                            <TextField
                                              name={`onboarding.${fieldId}.${key}`}
                                              value={
                                                values.onboarding?.[fieldId]?.[
                                                  key
                                                ]
                                              }
                                              label={key}
                                              variant="outlined"
                                              color="primary"
                                              margin="dense"
                                              fullWidth
                                              onBlur={handleBlur}
                                              onChange={handleChange}
                                              disabled={readOnly}
                                              InputProps={{
                                                ...InputProps
                                              }}
                                            />
                                          </Grid>
                                        );
                                      }
                                    )}
                                  </>
                                );
                              }

                              return <></>;
                            }
                          )}
                        </>
                      )}

                      <Grid item xs={12}>
                        <Button
                          ref={saveBoxRef}
                          fullWidth
                          color="success"
                          variant="contained"
                          type="submit"
                        >
                          Save
                        </Button>
                      </Grid>

                      {!saveBoxInView && (
                        <Box
                          sx={{
                            position: "fixed",
                            zIndex: 999,
                            bgcolor: "background.paper",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                            boxShadow: 3,
                            bottom: 0,
                            left: [0],
                            width: "100%"
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            <Button
                              fullWidth
                              color="success"
                              variant="contained"
                              type="submit"
                            >
                              Save
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </form>
                )}
              </Formik>
            </Grid>
          )}
        </Grid>

        {Boolean(loading) && <ModalLoadingFull />}
        {Boolean(processingMessage) && (
          <ModalLoadingMessage message={processingMessage} open />
        )}
      </Container>
    </Box>
  );
}
